import CancelIcon from "@mui/icons-material/Cancel";
import { Box, Typography, alpha } from "@mui/material";
import { useTranslation } from "react-i18next";
import Card from ".";
import colors from "../../config/colors";
import { useDimensions } from "../../hooks";
import { formatCurrency } from "../../utils";

const ErrorCard = ({
  totalAmount,
  errorMessage,
  reference,
  paymentReference,
  currency,
}: {
  totalAmount: string;
  errorMessage?: string;
  // The value the payer quotes to support, shown on the card and carried into
  // the support ticket. Absent only where the request that failed never
  // carried one -- the ticket is then one field thinner, not broken (D-17).
  reference?: string;
  // The reference that names the PAYMENT, shown beside the one that names the
  // failure. A refused collection has one; a payment that never reached the
  // platform has none, and then nothing renders here.
  paymentReference?: string;
  // The unit the pay response named, for the amount on the failure card.
  currency?: string;
}) => {
  // The hook rather than the module-level `t`: the header's language switcher
  // can change locale while this card is on screen, and only the hook
  // re-renders for it.
  const { t } = useTranslation();
  const { isSmall } = useDimensions();
  const maxWidth = isSmall ? "unset" : "38%";

  return (
    <Box sx={{ maxWidth }}>
      <Card
        sx={{
          display: "flex",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
          padding: isSmall ? 5 : 10,
        }}
      >
        <Box>
          <Box display={"flex"} justifyContent={"center"}>
            <CancelIcon color="error" sx={{ fontSize: 70 }} />
          </Box>

          <Typography
            fontSize={32}
            fontWeight={600}
            textAlign={"center"}
            color={"#D92D20"}
            paddingBottom={5}
          >
            {t("stepper_4.failed_transaction")}
          </Typography>
          <Typography
            textAlign={"center"}
            sx={{ fontWeight: "400", fontSize: 16, color: "#707070" }}
            paddingBottom={3}
          >
            {/* The payment item id is not a reference a payer or an agent can
                look a payment up by -- the PTN is, and the front end has none
                yet. Shown, it invited people to quote the wrong thing. */}
            {t("stepper_4.failed_message")}
          </Typography>
          {/* The amount lives in the journey context, which is empty when the
              payer reloads this page or opens it from a link -- and an
              unguarded formatter renders that absence as "0 FCFA". */}
          {Number(totalAmount) > 0 && (
            <Typography
              textAlign={"center"}
              fontSize={24}
              fontWeight={700}
              paddingBottom={2}
            >
              {formatCurrency(totalAmount, undefined, currency)}
            </Typography>
          )}
          {/* The reason is the one line a payer must not miss, so it is set
              apart from the grey text around it (MPAY-33410). */}
          <Typography
            textAlign={"center"}
            sx={{
              fontWeight: 600,
              fontSize: 17,
              color: "error.dark",
              bgcolor: theme => alpha(theme.palette.error.main, 0.1),
              borderRadius: 1,
              paddingX: 2,
              paddingY: 1.5,
              marginBottom: 3,
            }}
          >
            {/* No English default: an unresolved failure reaches the payer as
                the generic translated copy, in whichever locale they are on. */}
            {errorMessage || t("errors.unknown_error")}
          </Typography>
          {paymentReference && (
            <Box paddingBottom={2}>
              <Typography
                textAlign={"center"}
                sx={{ fontWeight: "400", fontSize: 14, color: colors.mutedTextColor }}
              >
                {t("payment_reference.label")}
              </Typography>
              <Typography
                textAlign={"center"}
                sx={{
                  fontWeight: "600",
                  fontSize: 14,
                  color: colors.mutedTextColor,
                  wordBreak: "break-all",
                  userSelect: "text",
                }}
              >
                {paymentReference}
              </Typography>
            </Box>
          )}
          {reference && (
            <Box paddingBottom={2}>
              <Typography
                textAlign={"center"}
                sx={{ fontWeight: "400", fontSize: 14, color: "#707070" }}
              >
                {t("stepper_4.reference")}
              </Typography>
              <Typography
                textAlign={"center"}
                sx={{
                  fontWeight: "600",
                  fontSize: 14,
                  color: "#707070",
                  // A UUID has no spaces to break on and the card is 38% of the
                  // viewport on desktop.
                  wordBreak: "break-all",
                  userSelect: "text",
                }}
              >
                {reference}
              </Typography>
              <Typography
                textAlign={"center"}
                sx={{ fontWeight: "400", fontSize: 14, color: "#707070" }}
                paddingTop={1}
              >
                {t("stepper_4.reference_help")}
              </Typography>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default ErrorCard;
